import { z } from 'zod';

import {
  DimensionItemSchema,
  FormattedCurrencySchema,
  MetricItemSchema,
  MetricScaleSchema,
} from './common.js';
import {
  DimensionsFilterSchema,
  FilterSchema,
  TimeFilterSchema,
} from './filters.js';

const MetricFilterServerSchema = z.object({
  id: z.string(),
  filter: z.array(z.string()),
});

// Recursive schema for widget data rows - allows any additional fields
const WidgetDataRowSchema: z.ZodType<{
  name: string;
  type: string;
  subRows?: unknown[];
  [key: string]: unknown;
}> = z.lazy(() =>
  z
    .object({
      name: z.string(),
      type: z.string(),
      subRows: z.array(WidgetDataRowSchema).optional(),
    })
    .catchall(z.unknown()),
);

export const WidgetDataResponseSchema = z
  .object({
    error: z
      .object({
        code: z.number(),
        message: z.string(),
        type: z.string(),
      })
      .optional(),
    name: z.string(),
    type: z.string(),
    subRows: z.array(WidgetDataRowSchema).optional(),
  })
  .catchall(z.unknown());

export const WidgetDataRequestSchema = z.object({
  companyId: z.string().optional(),
  metrics: z.array(z.string()),
  dimensions: z.array(DimensionItemSchema),
  dimensionFilters: z.array(DimensionsFilterSchema),
  currencyName: z.string().optional(),
  metricFilters: z.array(MetricFilterServerSchema),
  dimensionX: z.string().optional(),
  dimensionsUniques: z.string().optional(),
  chartType: z.string(),
  aggregationMetrics: z.array(z.string()).optional(),
  metricScale: MetricScaleSchema.optional(),
  missingValuesFill: z.string().optional(),
  dateFrom: z.string().nullable().optional(),
  dateTo: z.string().nullable().optional(),
  periodId: z.string().optional(),
  comparisonDateFrom: z.string().nullable().optional(),
  comparisonDateTo: z.string().nullable().optional(),
  dataBucketing: MetricScaleSchema.nullable().optional(),
  isAggregationAvailable: z.boolean().optional(),
  isAggregationOn: z.boolean().optional(),
  isTotalAggregationEnabled: z.boolean().optional(),
  timeFilter: TimeFilterSchema.optional(),
  filters: z.array(FilterSchema).optional(),
  metricsInfo: z.array(MetricItemSchema).optional(),
  currency: FormattedCurrencySchema.optional(),
});

// Allows any additional fields since we pre-transform to snake_case
export const WidgetDataRequestSchemaSnakeCase = z
  .object({
    company_id: z.string().optional(),
    metrics: z.array(z.string()),
    dimensions: z.array(z.unknown()),
    dimension_filters: z.array(z.unknown()).optional(),
    currency_name: z.string().optional(),
    metric_filters: z.array(z.unknown()).optional(),
    dimension_x: z.string().optional(),
    dimensions_uniques: z.string().optional(),
    chart_type: z.string(),
    aggregation_metrics: z.array(z.string()).optional(),
    metric_scale: z.string().optional(),
    missing_values_fill: z.string().nullable().optional(),
    // Time filter fields
    date_from: z.string().nullable().optional(),
    date_to: z.string().nullable().optional(),
    period_id: z.string().optional(),
    comparison_date_from: z.string().nullable().optional(),
    comparison_date_to: z.string().nullable().optional(),
    data_bucketing: z.string().nullable().optional(),
    is_aggregation_available: z.boolean().optional(),
    is_aggregation_on: z.boolean().optional(),
    is_total_aggregation_enabled: z.boolean().optional(),
    widget_id: z.string().optional(),
    widget_type: z.string().optional(),
  })
  .catchall(z.unknown());

export const WidgetDataExportRequestSchema = z.object({
  dateFrom: z.string().nullable().optional(),
  dateTo: z.string().nullable().optional(),
  periodId: z.string().optional(),
  comparisonDateFrom: z.string().nullable().optional(),
  comparisonDateTo: z.string().nullable().optional(),
  metrics: z.array(z.string()),
  metricFilters: z.array(MetricFilterServerSchema).optional(),
  dimensions: z.array(DimensionItemSchema),
  dimensionFilters: z.array(DimensionsFilterSchema).optional(),
  chartName: z.string().optional(),
  companyId: z.string().optional(),
  widgetId: z.string().optional(),
  widgetType: z.string().optional(),
});

export const WidgetDataExportResponseSchema = z.string();

export const ExportPivotTableFlowChartRequestSchema = z
  .object({
    company_id: z.string(),
    metrics: z.array(z.string()).optional().default([]),
    dimensions: z.array(z.unknown()),
    dimension_filters: z.array(z.unknown()).optional(),
    metric_filters: z.array(z.unknown()).optional(),
    date_from: z.string(),
    date_to: z.string(),
    widget_id: z.string().optional(),
    widget_type: z.string().optional(),
  })
  .catchall(z.unknown());
